import { BoloAdditionalInfo } from '@mark43/cad-api';
import { AdditionalInfoView } from '../types';

/**
 * Maps a BOLO-specific additional info from the API shape (`BoloAdditionalInfo`)
 * to the generic `AdditionalInfoView` used by the renderer and store.
 *
 * Field mapping rationale (BOLO -> AdditionalInfoView):
 *   - `boloLinkId`           -> `id`                 : unique identifier for the BOLO info entry
 *   - `additionalInfoId`     -> `agencyEventId`      : reuses the event ID field for renderer
 *                                                       compatibility; semantically this is NOT
 *                                                       an agency event ID for BOLOs
 *   - `boloNumber`           -> `agencyEventNumber`   : the BOLO identifier shown in the UI
 *   - `additionalInfoPriority` -> `priority`          : HIGH_PRIORITY_UPDATE | STANDARD_UPDATE
 *   - `type` is always 'USER'                         : BOLOs only support user-authored infos
 *   - `isEventNarrative` is always false              : BOLOs are not event narratives
 *   - `parentAgencyEventId/Number` are zeroed         : BOLOs have no cross-class parent concept
 *
 * TODO: RND-59848 - Introduce a discriminated union type
 * (e.g., EventAdditionalInfoView | BoloAdditionalInfoView) to avoid reusing
 * `agencyEventId` for a semantically different purpose. This becomes important
 * when pin/edit features land for BOLOs, as the field misuse will affect
 * edit flows that rely on `agencyEventId` to identify the owning entity.
 */
export const boloAdditionalInfoToView = (
    view: BoloAdditionalInfo,
    boloNumber: string
): AdditionalInfoView => {
    return {
        id: view.boloLinkId ?? -1,
        text: view.text,
        agencyEventId: view.additionalInfoId ?? -1,
        agencyEventNumber: boloNumber,
        priority: view.additionalInfoPriority,
        type: 'USER',
        isEventNarrative: false,
        isPinned: view.isPinned,
        pinnedByUserId: view.pinnedByUserId,
        pinnedDateUtc: view.pinnedDateUtc,

        createdDateUtc: view.createdDateUtc ?? '',
        authorCallSign: view.authorCallSign ?? '',
        authorUnitId: view.authorUnitId ?? 0,
        authorUserId: view.authorUserId ?? 0,
        userCadId: view.userCadId ?? '',
        userInitials: view.userInitials ?? '',

        parentAgencyEventId: 0,
        parentAgencyEventNumber: '',
    };
};
